import {getCookie} from 'common/util/cookie';

import {reporter} from '../error-reporting';
import {isLocalhostDevEnv, isTestEnv} from './env';

export interface UserIdentity {
  id: number;
  email: string;
  name: string;
}

/**
 * utf-8 compatible b64 decode
 * see https://stackoverflow.com/questions/30106476/using-javascripts-atob-to-decode-base64-doesnt-properly-decode-utf-8-strings
 * NOTE: not exported, if another consumer needs this, move to common/util/base64.ts
 */
function base64Decode(b64Str: string): string {
  return new TextDecoder().decode(Uint8Array.from(window.atob(b64Str), (c) => c.charCodeAt(0)));
}

export const userCookieUtils = {
  parseUserIdentityFromCookie,
  getUserIdentityFromCookie,
};

/** exported for tests only, should not be used directly, use getUserIdentityFromCookie() instead */
function parseUserIdentityFromCookie(): UserIdentity | null {
  // we don't have access to the mp_user cookie in localhost,
  // so just return some generic data to unblock development
  if (isLocalhostDevEnv()) {
    return {
      id: 1,
      email: `fake.user@email.com`,
      name: `Fake User`,
    };
  }

  let userIdentity = null;
  const userCookie = getCookie(`mp_user`);

  // user cookie format is b64(json({id,email,name}))
  if (userCookie) {
    try {
      userIdentity = JSON.parse(base64Decode(userCookie));
    } catch (err) {
      reporter.error(`Failed to parse user cookie: ${userCookie}`, err);
    }
  }

  return userIdentity;
}

let userIdentity: UserIdentity | null | undefined;

/** Returns user info from the "mp_user" cookie (cached once for the entire page load) */
function getUserIdentityFromCookie(): UserIdentity | null {
  if (isTestEnv()) {
    return parseUserIdentityFromCookie();
  }

  // only cache once, the cookie is not expected to change without a page refresh
  // this operation is cached because although it is not noticeably slow on its own, many repeated calls accumulate and pose a significant perf cost
  if (typeof userIdentity === `undefined`) {
    userIdentity = parseUserIdentityFromCookie();
  }

  return userIdentity;
}
